import theme from "@shared/utils/themeColors";
import { FormEvent, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import ModalBlank from "../../ui/modals/ModalBlank";

export type ImageType = "graph" | "table" | "illustration" | "image" | "info";

export interface ImageDetails {
  type: ImageType;
  title: string;
  caption: string;
}

interface ImageDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (details: ImageDetails) => void;
  fileName: string;
  initialDetails?: Partial<ImageDetails>;
}

export const ImageDetailsModal = ({
  isOpen,
  onClose,
  onSubmit,
  fileName,
  initialDetails = {},
}: ImageDetailsModalProps) => {
  // Use state initializers that will be updated when props change
  const [type, setType] = useState<ImageType>("image");
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const modalId = `image-details-modal-${uuidv4()}`;

  // Update state ONLY when the modal opens or when editing an existing image
  // Using a ref to track if we've already initialized the form for this modal session
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Only initialize form values when the modal first opens
    if (isOpen && !hasInitializedRef.current) {
      setType(initialDetails?.type || "image");
      setTitle(initialDetails?.title || "");
      setCaption(initialDetails?.caption || "");
      console.log("Modal opened with details:", initialDetails);
      hasInitializedRef.current = true;
    } else if (!isOpen) {
      // Reset the flag when modal closes
      hasInitializedRef.current = false;
    }
  }, [isOpen]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({
      type,
      title,
      caption,
    });
  };

  return (
    <ModalBlank id={modalId} modalOpen={isOpen} setModalOpen={() => onClose()}>
      <div
        className={`p-5 ${theme.bg.primary} rounded shadow-lg max-w-lg w-full mx-auto`}
      >
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-1">
            Image Details
          </h2>
          <p className={`text-sm ${theme.text.muted}`}>{fileName}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label
                className="block text-sm font-medium mb-1"
                htmlFor="image-type"
              >
                Image Type <span className={theme.text.error}>*</span>
              </label>
              <select
                id="image-type"
                className="form-select w-full"
                value={type}
                onChange={(e) => setType(e.target.value as ImageType)}
                required
              >
                <option value="image">Image</option>
                <option value="graph">Graph</option>
                <option value="table">Table</option>
                <option value="illustration">Illustration</option>
                <option value="info">Info</option>
              </select>
            </div>

            <div>
              <label
                className="block text-sm font-medium mb-1"
                htmlFor="image-title"
              >
                Title
              </label>
              <input
                id="image-title"
                className="form-input w-full"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter image title"
              />
            </div>

            <div>
              <label
                className="block text-sm font-medium mb-1"
                htmlFor="image-caption"
              >
                Caption
              </label>
              <textarea
                id="image-caption"
                className="form-textarea w-full"
                rows={3}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Enter image caption"
              />
            </div>

            <div className="flex justify-end space-x-2 pt-3">
              <button
                type="button"
                className="btn-sm border-gray-200 hover:border-gray-300 text-gray-600"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </ModalBlank>
  );
};
